import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Filter, X } from 'lucide-react';
import KBFilterPanel, {
  FileTypeFilter,
  DocumentTypeFilter,
  FileSizeFilter,
  KBCategorizedFilter,
  SourceFilter,
  DateRangeFilter,
} from '@/components/knowledge/KBFilterPanel';
import type { UseKBFiltersReturn } from '@/hooks/useKBFilters';

interface KBSearchAndFiltersProps {
  filters: UseKBFiltersReturn;
  dateRangeLabel?: string;
  searchPlaceholder?: string;
  /** Show file size min/max filters (default true) */
  showFileSizeFilter?: boolean;
  /** Show knowledge base categorized filter (default false) */
  showKBCategorizedFilter?: boolean;
  /** Extra elements rendered after the filter/clear buttons (e.g. bulk action buttons) */
  actions?: React.ReactNode;
  /** Extra classes for portaled dropdowns/popovers (e.g. page-scoped dark theming) */
  portalClassName?: string;
}

const KBSearchAndFilters: React.FC<KBSearchAndFiltersProps> = ({
  filters,
  dateRangeLabel = 'Date Range',
  searchPlaceholder = 'Search documents by name...',
  showFileSizeFilter = true,
  showKBCategorizedFilter = false,
  actions,
  portalClassName,
}) => {
  const {
    searchQuery,
    setSearchQuery,
    showFilters,
    setShowFilters,
    hasActiveFilters,
    activeFilterCount,
    handleResetFilters,
    // Filter panel props
    documentTypeFilter,
    handleDocumentTypeChange,
    filteredDocTypeOptions,
    documentTypesLoading,
    fileTypeFilter,
    handleFileTypeChange,
    filteredFileTypeOptions,
    minFileSize,
    maxFileSize,
    setMinFileSize,
    setMaxFileSize,
    kbCategorized,
    setKbCategorized,
    sourceFilter,
    setSourceFilter,
    dateRange,
    setDateRange,
  } = filters;

  return (
    <>
      {/* Search and Filter Buttons */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="relative flex-1 min-w-[300px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={searchQuery ? 'pl-10 pr-10' : 'pl-10'}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          variant={hasActiveFilters ? 'default' : 'outline'}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters {hasActiveFilters && `(${activeFilterCount})`}
        </Button>
        {hasActiveFilters && (
          <Button variant="outline" onClick={handleResetFilters}>
            Clear Filters
          </Button>
        )}
        {actions}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <KBFilterPanel>
          <DocumentTypeFilter
            value={documentTypeFilter}
            onChange={handleDocumentTypeChange}
            options={filteredDocTypeOptions}
            loading={documentTypesLoading}
            portalClassName={portalClassName}
          />
          <FileTypeFilter
            value={fileTypeFilter}
            onChange={handleFileTypeChange}
            options={filteredFileTypeOptions}
            portalClassName={portalClassName}
          />
          {showFileSizeFilter && (
            <FileSizeFilter
              minValue={minFileSize}
              maxValue={maxFileSize}
              onMinChange={setMinFileSize}
              onMaxChange={setMaxFileSize}
            />
          )}
          {showKBCategorizedFilter && (
            <KBCategorizedFilter
              value={kbCategorized}
              onChange={setKbCategorized}
              portalClassName={portalClassName}
            />
          )}
          <SourceFilter
            value={sourceFilter}
            onChange={setSourceFilter}
            portalClassName={portalClassName}
          />
          <DateRangeFilter
            value={dateRange}
            onChange={setDateRange}
            label={dateRangeLabel}
            portalClassName={portalClassName}
          />
        </KBFilterPanel>
      )}
    </>
  );
};

export default KBSearchAndFilters;
